import { Edit } from '@mui/icons-material';
import { Button, Divider, Grid, Theme, useMediaQuery } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useState, FC, useContext } from 'react';
import { useHistory } from 'react-router-dom';

import { Page, PageTitleWithBack, Pagination, Table } from '../../../components';
import { getSpecs } from '../../../fetch';
import { SALES_PORTAL_PER_PAGE_OPTIONS, formatMoney, getEnvironment } from '../../../helpers';
import { ISpec, ILot } from '../../../models';
import { ManageSpecFilters } from './ManageSpecFilters';
import { MobileSpec } from './MobileSpecs';
import { SalesContext, update } from '../../../context/sales';
import { getCommonNeighborhoodLots } from '../../../fetch/lookups';

interface IManageSpecs {
  path?: string;
  backText?: string;
}

const PAGE_NAME = 'manage-specs';
export const ManageSpecs: FC<IManageSpecs> = ({ path, backText }) => {
  const { enqueueSnackbar } = useSnackbar();
  const removeStickyHeader = useMediaQuery('(max-width: 1275px)');
  const [specs, setSpecs] = useState<ISpec[]>([]);
  const [isLoadingSpecs, setLoadingSpecs] = useState<boolean>(true);
  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const { perPageSalesPortalValues, dispatchPerPage } = useContext(SalesContext);
  const currentPageState = perPageSalesPortalValues.find(
    state => state?.pageName === PAGE_NAME
  )?.itemsPerPage;
  const perPageContext = useMemo<number>(
    () => (currentPageState && currentPageState >= 25 ? currentPageState : 25),
    [currentPageState]
  );

  const backToSalesDashboard = '/sales';
  const isFromSales = path?.includes('sales');
  const [perPage, setRowsPerPage] = useState<number>(10);
  const [selectedNeighborhoodId, setSelectedNeighborhoodId] = useState<string>('');
  const [selectedLotId, setSelectedLotId] = useState<string>('');
  const [lots, setLots] = useState<ILot[]>(null);
  const [isLoadingNeighborhoodLots, setNeighborhoodsLotsLoading] = useState<boolean>(false);
  const [searchValue, setSearch] = useState<string>('');
  const [searchedValue, setSearchedValue] = useState<string>('');

  const [filters, setFilters] = useState<{
    neighborhoodId?: number | string;
    lotNumber?: number | string;
  } | null>({});

  const [selectedSort, setSelectedSort] = useState<string>('Name');
  const [sortDirection, setSortDirection] = useState<{
    neighborhoodDescription?: 'Asc' | 'Desc';
    lotNumber?: 'Asc' | 'Desc';
    lotPremium?: 'Asc' | 'Desc';
    planDescription?: 'Asc' | 'Desc';
    basePrice?: 'Asc' | 'Desc';
    jobCode?: 'Asc' | 'Desc';
    upgradePrice?: 'Asc' | 'Desc';
    totalPrice?: 'Asc' | 'Desc';
  }>({
    neighborhoodDescription: 'Asc'
  });

  const history = useHistory();

  const isMobile = useMediaQuery('(max-width: 960px)');

  const classes = useStyles();

  const fetchSpecs = async () => {
    try {
      setLoadingSpecs(true);
      const res = await getSpecs({
        page: page + 1,
        sortBy: selectedSort,
        sortDirection: sortDirection[selectedSort] as string,
        perPage: isFromSales ? perPageContext : perPage,
        neighborhoodId: selectedNeighborhoodId,
        lotNumber: selectedLotId,
        search: searchedValue,
        isForViewSpecs: true
      });
      setRecordCount(res.totalRecordCount);
      setSpecs(res.records);
    } catch (error) {
      enqueueSnackbar(`Error loading specs, please try again.`, {
        variant: 'error'
      });
      console.log(error);
    } finally {
      setLoadingSpecs(false);
    }
  };

  const fetchNeighborhoodLots = async () => {
    setNeighborhoodsLotsLoading(true);
    try {
      const res = await getCommonNeighborhoodLots({
        neighborhoodId: Number(selectedNeighborhoodId)
      });
      setLots(res);
    } catch (error) {
      console.error(error);
    }
    setNeighborhoodsLotsLoading(false);
  };

  const handleClickColumn = (column: string) => {
    setSelectedSort(column);
    setSortDirection({
      ...sortDirection,
      [column]: sortDirection[column] === 'Asc' ? 'Desc' : 'Asc'
    });
  };

  useEffect(() => {
    fetchSpecs();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPageContext, perPage, searchedValue, filters, sortDirection, selectedSort]);

  useEffect(() => {
    if (selectedNeighborhoodId) {
      fetchNeighborhoodLots();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNeighborhoodId]);

  const columns = useMemo(() => {
    return [
      {
        Header: 'Neighborhood',
        accessor: 'neighborhoodDescription',
        isServerSorted: selectedSort === 'neighborhoodDescription',
        isServerSortedDesc: sortDirection.neighborhoodDescription === 'Desc',
        handleClickColumn: () => handleClickColumn('neighborhoodDescription')
      },
      {
        Header: 'Lot Number',
        accessor: 'lotNumber',
        isServerSorted: selectedSort === 'lotNumber',
        isServerSortedDesc: sortDirection.lotNumber === 'Desc',
        handleClickColumn: () => handleClickColumn('lotNumber')
      },
      {
        Header: 'Plan',
        accessor: 'planDescription',
        isServerSorted: selectedSort === 'planDescription',
        isServerSortedDesc: sortDirection.planDescription === 'Desc',
        handleClickColumn: () => handleClickColumn('planDescription')
      },
      {
        Header: 'Base Price',
        accessor: 'basePrice',
        isServerSorted: selectedSort === 'basePrice',
        isServerSortedDesc: sortDirection.basePrice === 'Desc',
        handleClickColumn: () => handleClickColumn('basePrice'),
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: ISpec } };
        }) => {
          return <>{formatMoney(original.basePrice)}</>;
        }
      },
      {
        Header: 'Upgrades',
        accessor: 'upgradePrice',
        isServerSorted: selectedSort === 'upgradePrice',
        isServerSortedDesc: sortDirection.upgradePrice === 'Desc',
        handleClickColumn: () => handleClickColumn('upgradePrice'),
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: ISpec } };
        }) => {
          return <>{formatMoney(original.upgradePrice)}</>;
        }
      },
      {
        Header: 'Lot Premium',
        accessor: 'lotPremium',
        isServerSorted: selectedSort === 'lotPremium',
        isServerSortedDesc: sortDirection.lotPremium === 'Desc',
        handleClickColumn: () => handleClickColumn('lotPremium'),
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: ISpec } };
        }) => {
          return <>{formatMoney(original.lotPremium)}</>;
        }
      },
      {
        Header: 'Total Price',
        accessor: 'totalPrice',
        isServerSorted: selectedSort === 'totalPrice',
        isServerSortedDesc: sortDirection.totalPrice === 'Desc',
        handleClickColumn: () => handleClickColumn('totalPrice'),
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: ISpec } };
        }) => {
          return <>{formatMoney(original.totalPrice)}</>;
        }
      },
      {
        id: 'actions',
        accessor: '',
        Header: '',
        overrideWidth: 200,
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: ISpec } };
        }) => {
          return (
            <>
              <Button
                id="edit"
                color="primary"
                startIcon={<Edit />}
                onClick={() => {
                  history.push(`${path ?? '/development'}/specs/${original.buildId}`);
                }}
              >
                Edit
              </Button>
            </>
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [specs]);
  return (
    <>
      <Page pageHasStickyHeaders={removeStickyHeader ? false : true} title="Manage Specs">
        <Grid container>
          <Grid item xs={12} className={classes.pageTitleWrapper}>
            <PageTitleWithBack
              showDivider={false}
              backText={backText ?? `Back to Admin home`}
              backUrl={backToSalesDashboard}
              title="Manage Specs"
            />
          </Grid>
        </Grid>
        <Divider />
        <ManageSpecFilters
          isLoading={isLoadingSpecs}
          selectedNeighborhoodId={selectedNeighborhoodId}
          selectedLotId={selectedLotId}
          lots={lots}
          setSelectedNeighborhoodId={setSelectedNeighborhoodId}
          isLoadingNeighborhoodLots={isLoadingNeighborhoodLots}
          setSelectedLotId={setSelectedLotId}
          handleSearch={(clearSearch?: boolean) => {
            setPage(0);
          }}
          applyFilters={(clearFilters?: boolean) => {
            if (clearFilters) {
              setFilters({
                neighborhoodId: '',
                lotNumber: ''
              });
            } else {
              setPage(0);
              setFilters({
                ...filters,
                neighborhoodId: selectedNeighborhoodId || undefined,
                lotNumber: selectedLotId || undefined
              });
            }
          }}
          searchValue={searchValue}
          searchedValue={searchedValue}
          setSearch={setSearch}
          setSearchedValue={setSearchedValue}
          setPage={setPage}
          fetchSpecs={fetchSpecs}
        />

        <Table
          stickyHeaders={removeStickyHeader ? false : true}
          columns={columns}
          data={specs}
          isLoading={isLoadingSpecs}
          hidePagination
          mobileProps={{
            handleEdit: (val: ISpec) => {
              history.push(`${path ?? '/development'}/specs/${val.buildId}`);
            }
          }}
          ResponsiveComponent={isMobile ? MobileSpec : undefined}
        />
        {!isLoadingSpecs && (
          <Pagination
            page={page}
            count={recordCount}
            rowsPerPage={isFromSales ? perPageContext : perPage}
            setPage={setPage}
            rowsPerPageOptions={isFromSales ? SALES_PORTAL_PER_PAGE_OPTIONS : undefined}
            setRowsPerPage={itemsPerPage => {
              if (isFromSales) {
                return dispatchPerPage(
                  update({
                    pageName: PAGE_NAME,
                    environment: getEnvironment(),
                    itemsPerPage: itemsPerPage
                  })
                );
              } else {
                setRowsPerPage(itemsPerPage);
              }
            }}
          />
        )}
      </Page>
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  wrapper: {
    marginTop: '1rem'
  },
  addNeighborhoodWrapper: {
    marginBottom: theme.spacing(1)
  },
  addNeighborhoodButton: {
    marginTop: theme.spacing(1),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: 'auto'
    }
  },
  pageTitleWrapper: {
    '@media (min-width: 768px)': {
      flex: 1
    }
  },

  infoWrapper: {
    '@media (min-width: 768px)': {
      textAlign: 'right'
    }
  },
  editButton: {
    textAlign: 'right'
  },
  deleteButton: {
    marginLeft: theme.spacing(0.5),
    color: theme.palette.error.main
  },
  addWrapper: {
    marginBottom: theme.spacing(1)
  },
  addButton: {
    alignSelf: 'flex-start',
    marginTop: theme.spacing(1),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      marginTop: 0,
      width: 'auto'
    }
  }
}));
